import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Play, Lock, MessageSquare, Send, Trash2, Share2, Video, ShoppingCart, ArrowUp, ChevronRight, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcademiaContent = () => {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    fetchContent();
    checkAuth();
  }, [contentId]);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Auth error:', error);
      }
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const response = await axios.get(`${BACKEND_URL}/api/content/${contentId}`, { headers });
      setContent(response.data);

      // Register view if user has access
      if (response.data.has_access && token) {
        axios.post(`${BACKEND_URL}/api/content/${contentId}/view`, {}, { headers }).catch(() => {});
      }

      // Fetch comments
      if (token) {
        fetchComments();
      }

      // Fetch related content from same category
      const category = response.data.category || response.data.academia_category || 'academia';
      try {
        const relToken = localStorage.getItem('session_token');
        const relRes = await axios.get(`${BACKEND_URL}/api/content?category=${category}`, {
          headers: relToken ? { Authorization: `Bearer ${relToken}` } : {}
        });
        setRelated((relRes.data || []).filter(c => c.content_id !== contentId).slice(0, 6));
      } catch { /* ignore */ }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Contenido no encontrado');
        navigate('/academia');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    setLoadingComments(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content/${contentId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    const token = localStorage.getItem('session_token');
    if (!token) { toast.error('Debes iniciar sesión para comentar'); return; }

    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/content/${contentId}/comments`,
        { text: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setComments(prev => [response.data.comment, ...prev]);
        setNewComment('');
        toast.success('Comentario publicado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar comentario');
    } finally {
      setSubmittingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      toast.success('Comentario eliminado');
    } catch {
      toast.error('Error al eliminar comentario');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: content?.title, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado al portapapeles');
    }
  };

  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  };

  const renderVideoPlayer = () => {
    const url = content.external_url || content.file_path || '';

    if (url.includes('youtube') || url.includes('youtu.be')) {
      const embedUrl = url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
      return (
        <iframe
          src={embedUrl}
          className="w-full h-full"
          allowFullScreen
          allow="autoplay; encrypted-media"
          title={content.title}
        />
      );
    }

    if (url.includes('drive.google.com')) {
      const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match) {
        return (
          <iframe
            src={`https://drive.google.com/file/d/${match[1]}/preview`}
            className="w-full h-full"
            allowFullScreen
            allow="autoplay; encrypted-media"
            title={content.title}
          />
        );
      }
    }

    return (
      <video
        controls
        autoPlay
        className="w-full h-full"
        src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`}
      >
        Tu navegador no soporta la reproducción de videos.
      </video>
    );
  };

  const canAccessRelated = (item) => {
    const contentLevels = item.levels || [item.level];
    if (!user) return contentLevels.includes('free') || item.level === 'free';
    if (user.has_academia_access) return true;
    const accessLevels = {
      'free': ['free'],
      'principiante': ['free', 'nivel_1'],
      'intermedio': ['free', 'nivel_1', 'nivel_2'],
      'avanzado': ['free', 'nivel_1', 'nivel_2', 'nivel_3'],
      'premium': ['free', 'nivel_1', 'nivel_2', 'nivel_3', 'premium']
    };
    const userAllowedLevels = accessLevels[user.subscription_level] || ['free'];
    return contentLevels.some(lvl => userAllowedLevels.includes(lvl));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-400 text-lg">Contenido no encontrado</p>
        <Button onClick={() => navigate('/academia')} className="bg-pink-600 hover:bg-pink-500 text-white rounded-full">
          Volver a la Academia
        </Button>
      </div>
    );
  }

  const levelNames = { 'free': 'Gratis', 'nivel_1': 'Nivel 1', 'nivel_2': 'Nivel 2', 'premium': 'Premium' };

  return (
    <div className="min-h-screen bg-[#09090b] py-24" data-testid="academia-content-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Back + Share */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/academia')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="back-to-academia-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Volver a la Academia</span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-zinc-400 hover:text-pink-400 transition-colors"
            data-testid="share-content-btn"
          >
            <Share2 className="w-5 h-5" />
            <span className="text-sm font-medium hidden md:inline">Compartir</span>
          </button>
        </div>

        {/* Video / Content Area */}
        {content.has_access ? (
          <>
            {content.content_type === 'video' ? (
              <div className="bg-zinc-900 rounded-2xl overflow-hidden aspect-video border border-zinc-800 mb-6" data-testid="video-player">
                {renderVideoPlayer()}
              </div>
            ) : content.content_type === 'image' ? (
              <div className="rounded-2xl overflow-hidden border border-zinc-800 mb-6">
                <img
                  src={(content.cover_image || content.file_path || '').startsWith('/api') ? `${BACKEND_URL}${content.cover_image || content.file_path}` : (content.cover_image || content.file_path)}
                  alt={content.title}
                  className="w-full max-h-[600px] object-contain bg-zinc-900"
                />
              </div>
            ) : (
              <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 mb-6 text-center">
                <a
                  href={(content.file_path || '').startsWith('http') ? content.file_path : `${BACKEND_URL}${content.file_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-4 font-bold transition-colors"
                >
                  <Play className="w-5 h-5" />
                  Descargar Archivo
                </a>
              </div>
            )}
          </>
        ) : (
          /* Locked state */
          <div className="bg-zinc-900 rounded-2xl border border-zinc-800 mb-6 aspect-video flex flex-col items-center justify-center relative overflow-hidden" data-testid="content-locked">
            {(content.cover_image || content.thumbnail_url) && (
              <img
                src={(content.cover_image || content.thumbnail_url).startsWith('/api') ? `${BACKEND_URL}${content.cover_image || content.thumbnail_url}` : (content.cover_image || content.thumbnail_url)}
                alt={content.title}
                className="absolute inset-0 w-full h-full object-cover opacity-20 blur-sm"
              />
            )}
            <div className="relative z-10 flex flex-col items-center gap-4 p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                <Lock className="w-10 h-10 text-zinc-500" />
              </div>
              <h3 className="text-xl font-bold text-white">Contenido Bloqueado</h3>
              <p className="text-zinc-400 max-w-md">Necesitas una suscripción activa para acceder a este contenido.</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Link to="/programs">
                  <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-3 font-bold" data-testid="upgrade-btn">
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Ver Programas
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Title & Description */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${
              content.content_type === 'video' ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30' :
              content.content_type === 'image' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
              'bg-blue-500/20 text-blue-400 border border-blue-500/30'
            }`}>
              {content.content_type === 'video' ? 'Video' : content.content_type === 'image' ? 'Imagen' : 'PDF'}
            </span>
            {content.academia_category && (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700 capitalize">
                {content.academia_category}
              </span>
            )}
            {(content.levels || [content.level]).map(l => (
              <span key={l} className="text-xs font-medium px-3 py-1 rounded-full bg-zinc-800 text-zinc-500 border border-zinc-700">
                {levelNames[l] || l}
              </span>
            ))}
            {content.view_count > 0 && (
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <Eye className="w-3 h-3" /> {content.view_count}
              </span>
            )}
          </div>
          <h1 className="font-barlow text-2xl md:text-4xl font-bold text-white mb-3" data-testid="content-title">
            {content.title}
          </h1>
          {content.description && (
            <p className="text-zinc-400 text-base md:text-lg whitespace-pre-line" data-testid="content-description">
              {content.description}
            </p>
          )}
        </div>

        {/* Comments Section */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800 mb-12" data-testid="comments-section">
          <h4 className="text-white font-bold flex items-center gap-2 mb-5 text-lg">
            <MessageSquare className="w-5 h-5 text-pink-400" />
            Comentarios ({comments.length})
          </h4>

          {user && content.has_access && (
            <div className="flex gap-2 mb-6">
              <Input
                type="text"
                placeholder="Escribe tu comentario..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitComment()}
                className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                maxLength={1000}
                data-testid="comment-input"
              />
              <Button
                onClick={submitComment}
                disabled={submittingComment || !newComment.trim()}
                className="bg-pink-600 hover:bg-pink-500 px-4"
                data-testid="submit-comment-btn"
              >
                {submittingComment ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          )}

          {loadingComments ? (
            <div className="flex justify-center py-6">
              <div className="w-6 h-6 border-2 border-pink-500/30 border-t-pink-500 rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-6">
              No hay comentarios aún. {user ? '¡Sé la primera en comentar!' : 'Inicia sesión para comentar.'}
            </p>
          ) : (
            <div className="space-y-3">
              {comments.map(comment => (
                <div key={comment.comment_id} className="bg-zinc-800/50 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-pink-400 text-sm">{comment.user_name}</span>
                        <span className="text-zinc-500 text-xs">{formatRelativeTime(comment.created_at)}</span>
                      </div>
                      <p className="text-zinc-300 text-sm">{comment.text}</p>
                    </div>
                    {comment.can_delete && (
                      <button
                        onClick={() => deleteComment(comment.comment_id)}
                        className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                        title="Eliminar comentario"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related Content */}
        {related.length > 0 && (
          <div className="mb-12">
            <h3 className="font-barlow text-xl font-bold text-white mb-6 flex items-center gap-2">
              <ChevronRight className="w-5 h-5 text-pink-400" />
              Más contenido de la Academia
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map(item => {
                const accessible = canAccessRelated(item);
                return (
                  <Link
                    to={`/academia/${item.content_id}`}
                    key={item.content_id}
                    className="group bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden hover:border-pink-500/50 transition-all"
                    data-testid={`related-content-${item.content_id}`}
                  >
                    <div className="aspect-video bg-zinc-800 relative flex items-center justify-center overflow-hidden">
                      {(item.cover_image || item.thumbnail_url) ? (
                        <img
                          src={(item.cover_image || item.thumbnail_url).startsWith('/api') ? `${BACKEND_URL}${item.cover_image || item.thumbnail_url}` : (item.cover_image || item.thumbnail_url)}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <Video className="w-10 h-10 text-zinc-600" />
                      )}
                      {!accessible && (
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center">
                          <Lock className="w-8 h-8 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-white text-sm line-clamp-2 group-hover:text-pink-400 transition-colors">
                        {item.title}
                      </h4>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademiaContent;
